import {
  InputLooks,
  StandardTextfield,
} from '@brandfolder/react';
import { t } from '@lingui/macro';
import React, { FunctionComponent, RefObject, useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import useDebounce from '@components/common/custom_hooks/useDebounce';
import { SuggestedNamesList } from '@components/people_tags/common/SuggestedNamesList';
import { Person } from '@components/people_tags/customPeopleTagTypes';

import { useMergeIdentities } from './hooks/useMergeIdentities';
import { useUpdateFaceName } from './hooks/useUpdateFaceName';
import { MergePeopleModal } from './MergePeopleModal';

const PeopleNameTextfieldWrapper = styled.div`
  position: relative;
  width: 100%;

  .person-details-suggested-names-list {
    width: 100%;
  }
`;

export interface PeopleNameTextfieldProps {
  setPersonLabel: (personLabel: string) => void;
  availablePeople: Person[];
  currentPersonId?: string;
  currentPersonName?: string;
  currentPersonImage?: string;
  onSelectTargetPerson?: (person: Person) => void;
  onNameChange?: (name: string) => void;
  cancelEdit: () => void;
  onUpdateSuccess?: () => void;
  placeholder?: string;
  searchTag?: string;
  showLabel?: boolean;
  textfieldRef?: RefObject<HTMLInputElement>;
  validateInput?: (value: string) => string;
}

export const PeopleNameTextfield: FunctionComponent<PeopleNameTextfieldProps> = ({
  setPersonLabel,
  availablePeople,
  cancelEdit,
  currentPersonId = undefined,
  currentPersonName = undefined,
  currentPersonImage = undefined,
  onUpdateSuccess = undefined,
  placeholder = undefined,
  searchTag = undefined,
  showLabel = false,
  textfieldRef = undefined,
  validateInput = undefined
}) => {
  const [localValue, setLocalValue] = useState(searchTag || '');
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  const [targetPerson, setTargetPerson] = useState<Person | undefined>();
  const { mergeIdentities } = useMergeIdentities(onUpdateSuccess);
  const { updateFaceName } = useUpdateFaceName(onUpdateSuccess);

  // Debounce the local value with 300ms delay
  const debouncedValue = useDebounce({
    delay: 300,
    disableDebounce: process?.env?.NODE_ENV === 'circle',
    value: localValue,
  });

  // Update parent component when debounced value changes
  useEffect(() => {
    if (debouncedValue !== searchTag) {
      setPersonLabel(debouncedValue);
    }
  }, [debouncedValue, setPersonLabel, searchTag]);

  // Handle input change with immediate validation
  const handleInputChange = useCallback((value: string) => {
    const validatedValue = validateInput ? validateInput(value) : value;
    setLocalValue(validatedValue);
  }, [validateInput]);

  // Filter local people data based on search term
  const filteredPeople = useMemo(() => {
    if (!debouncedValue || !debouncedValue.trim()) {
      return [];
    }

    const searchTerm = debouncedValue.toLowerCase();
    return availablePeople.filter((person) => {
      // Exclude current person from suggestions
      if (currentPersonId === person.person_id) {
        return false;
      }
      // Filter by search term
      return person.tag_name.toLowerCase().includes(searchTerm);
    });
  }, [availablePeople, debouncedValue, currentPersonId]);

  // Show create option when user has typed something AND it's different from current name
  const shouldShowCreateOption = useMemo(() => {
    if (!debouncedValue || !debouncedValue.trim()) {
      return false;
    }

    // Don't show create option if the typed name is the same as current person's name
    if (currentPersonName && debouncedValue.trim().toLowerCase() === currentPersonName.toLowerCase()) {
      return false;
    }

    return true;
  }, [debouncedValue, currentPersonName]);

  // Show dropdown when there are suggestions OR create option should be shown
  const shouldShowDropdown = useMemo(() => {
    if (!debouncedValue || !debouncedValue.trim()) {
      return false;
    }

    // Show dropdown if there are filtered people OR create option should be shown
    return filteredPeople.length > 0 || shouldShowCreateOption;
  }, [debouncedValue, filteredPeople.length, shouldShowCreateOption]);

  // Removed showAutocompleteResults state and effects - simplified to just check if user has typed something

  const handleOnSelectTargetPerson = (person: Person): void => {
    // Check if the selected person has the same name as what's being typed
    if (debouncedValue && debouncedValue.trim().toLowerCase() === person.tag_name.toLowerCase()) {
      // Show merge dialog
      setTargetPerson(person);
      setShowMergeDialog(true);
    } else {
      if (currentPersonId && updateFaceName) {
        updateFaceName({ personId: currentPersonId, name: person.tag_name, old_name: currentPersonName });
      }

      setLocalValue(person.tag_name);
      setPersonLabel(person.tag_name);
      cancelEdit();

    }
  };

  const handleCreateNewPerson = useCallback((name: string): void => {
    // Check if there's an existing person with the same name
    const existingPerson = availablePeople.find((person) =>
      person.tag_name.toLowerCase() === name.toLowerCase()
    );

    if (existingPerson) {
      // Show merge dialog
      setTargetPerson(existingPerson);
      setShowMergeDialog(true);
    } else {
      // Update identity name (which creates/names the identity)
      if (currentPersonId && updateFaceName) {
        void updateFaceName({ personId: currentPersonId, name, old_name: currentPersonName }).then((success) => {
          if (success) {
            setLocalValue(name);
            setPersonLabel(name);
          }
        });
      }
    }
  }, [availablePeople, currentPersonId, currentPersonName, updateFaceName, setPersonLabel]);

  const handleMerge = useCallback((): void => {
    if (targetPerson && currentPersonId) {
      if (mergeIdentities) {
        void mergeIdentities({
          sourceIdentityId: currentPersonId,
          destinationIdentityId: targetPerson.person_id,
          destinationName: targetPerson.tag_name.trim(),
          sourceName: currentPersonName,
        }).then((success) => {
          if (success) {
            cancelEdit();
          }
          setShowMergeDialog(false);
          setTargetPerson(undefined);
        });
      } else {
        setShowMergeDialog(false);
        setTargetPerson(undefined);
        cancelEdit();
      }
    } else {
      setShowMergeDialog(false);
      setTargetPerson(undefined);
    }
  }, [targetPerson, currentPersonId, mergeIdentities, cancelEdit, debouncedValue]);

  const handleCancelMerge = useCallback((): void => {
    // Update identity name when canceling merge (keeping them separate)
    if (targetPerson && currentPersonId && updateFaceName && debouncedValue) {
      void updateFaceName({
        personId: currentPersonId,
        name: debouncedValue.trim(),
        old_name: currentPersonName
      }).then(() => {
        cancelEdit();
      });
    }
    setShowMergeDialog(false);
    setTargetPerson(undefined);
  }, [targetPerson, currentPersonId, currentPersonName, updateFaceName, debouncedValue, cancelEdit]);

  return (
     <>
      <PeopleNameTextfieldWrapper tabIndex={-1}>
        <StandardTextfield
          ref={textfieldRef}
          className="tag-person-textfield"
          id="textfield"
          label="Name"
          look={InputLooks.Default}
          onChange={(e): void => handleInputChange(e.target.value)}
          placeholder={placeholder || t`Add a name`}
          showLabel={showLabel}
          textfieldProps={{
            autoFocus: true,
            autoComplete: 'off',
            className: 'textfield'
          }}
          type="text"
          value={localValue}
        />
        {shouldShowDropdown && (
          <SuggestedNamesList
            handleCreateNewPerson={handleCreateNewPerson}
            handleOnSelectTargetPerson={handleOnSelectTargetPerson}
            listClasses="person-details-suggested-names-list"
            showCreateOption={shouldShowCreateOption}
            suggestions={filteredPeople}
            typedName={localValue}
          />
        )}
      </PeopleNameTextfieldWrapper>

      {showMergeDialog && targetPerson && (
        <MergePeopleModal
          currentPersonThumbnail={currentPersonImage || ''}
          handleCancelMerge={handleCancelMerge}
          handleMerge={handleMerge}
          isOpen={showMergeDialog}
          targetPersonThumbnail={targetPerson.face_uri}
        />
      )}
    </>
  );
};
